import ComponentManager from "../../componentFramework";
import EventListenerManager from "../EventListenerManager";
import deepFreeze from "../../scripts/deepFreeze";
import deepCopy from "../../scripts/deepCopy";

export interface TooltipOptions {
	viewGutter: number,
	pointerOffset: number,
	hideOnClick: boolean
}

/**
 */
@ComponentManager.register()
export default class Tooltip<O extends TooltipOptions> {
	public static componentStyleClass = "tooltip";
	private displayElem: HTMLElement | undefined;
	private tooltipText: string | undefined;
	private mouseOver = false;

	protected readonly eventListenerManager = new EventListenerManager();

	public static readonly defaultOptions: TooltipOptions = deepFreeze({
		viewGutter: 20,
		pointerOffset: 5,
		hideOnClick: true
	});
	protected readonly options: O;

	constructor(private readonly rootElement: HTMLElement, defaultOptions?: O, options?: Partial<O>) {
		this.options = deepCopy([defaultOptions ?? Tooltip.defaultOptions, options]) as O;

		const managedElement = this.eventListenerManager.manage(rootElement);
		managedElement.addEventListener("mousemove", ev => this.updateDisplayPosition(ev.pageX, ev.pageY));

		const onMouseOut = () => {
			this.mouseOver = false;
			this.hide();
		};
		managedElement.addEventListener("mouseout", onMouseOut);

		managedElement.addEventListener("mouseover", ev => {
			this.mouseOver = true;
			this.show();
			this.updateDisplayPosition(ev.pageX, ev.pageY);
		});

		if (this.options.hideOnClick) {
			managedElement.addEventListener("click", onMouseOut);
		}
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.hide();
		this.eventListenerManager.removeListeners();
	}

	protected isMouseOver() {
		return this.mouseOver;
	}

	protected updateDisplayPosition(eventX: number, eventY: number) {
		const displayElem = this.getDisplayElement();
		if (displayElem !== undefined) {
			const coordinate = this.determineCoordinatesForDisplayElement(eventX, eventY);
			if (coordinate.x !== undefined) {
				displayElem.style.left = coordinate.x + "px";
			}
			if (coordinate.y !== undefined) {
				displayElem.style.top = coordinate.y + "px";
			}
		}
	}

	protected determineCoordinatesForDisplayElement(eventX: number, eventY: number): {x?: number, y?: number} {
		let x = undefined;
		let y = undefined;
		const displayElem = this.getDisplayElement();
		if (displayElem !== undefined) {
			const options = this.getOptions();
			const elem = this.getElement();

			const gutter = options.viewGutter;
			const offset = options.pointerOffset ?? 0;
			const docElem = elem.ownerDocument.documentElement;

			// Determine X position and potentially set max-width
			const scrollX = window.scrollX || window.pageXOffset;
			x = eventX + offset;
			displayElem.style.removeProperty("maxWidth");
			if (displayElem.offsetWidth >= docElem.clientWidth / 2) {
				displayElem.style.maxWidth = (docElem.clientWidth - (gutter * 2)) + "px";
				x = eventX - (displayElem.offsetWidth / 2);
				x = Math.max(gutter, x);
				const amountOverRightEdge = (x + displayElem.offsetWidth + gutter) - docElem.clientWidth;
				if (amountOverRightEdge > 0) {
					x -= amountOverRightEdge;
				}
			} else {
				if (x - scrollX + displayElem.offsetWidth + gutter >= docElem.clientWidth) {
					if (x > displayElem.offsetWidth) {
						x = x - displayElem.offsetWidth - (offset * 2);
					}
				}
			}

			// Determine Y position
			const scrollY = window.scrollY || window.pageYOffset;
			y = eventY + offset;
			if (y - scrollY + displayElem.offsetHeight + gutter >= docElem.clientHeight) {
				y = y - displayElem.offsetHeight - (offset * 2);
			}
		}
		return {
			x: x,
			y: y
		};
	}

	protected getOptions() {
		return this.options;
	}

	protected getElement(): HTMLElement {
		return this.rootElement;
	}

	protected getDisplayElement(): HTMLElement | undefined {
		if (this.displayElem === undefined) {
			const elem = this.getElement();
			const tooltipText = this.getTooltipText();
			if (tooltipText !== undefined) {
				this.displayElem = elem.ownerDocument.createElement("div");
				this.displayElem.classList.add("tooltipDisplay");
				const textNode = elem.ownerDocument.createTextNode(tooltipText);
				this.displayElem.appendChild(textNode);
			}
		}
		return this.displayElem;
	}

	public setTooltipText(text: string) {
		this.tooltipText = text;
		this.getElement().removeAttribute("title");
		if (this.displayElem !== undefined && this.displayElem.nodeValue !== text) {
			this.displayElem.nodeValue = text;
		}
	}
	public getTooltipText(): string | undefined {
		if (this.tooltipText === undefined) {
			const elem = this.getElement();
			const titleAttrVal = elem.getAttribute("title");
			if (titleAttrVal !== null && titleAttrVal.length > 0) {
				this.tooltipText = titleAttrVal.trim();
				elem.removeAttribute("title");
			}
		}
		return this.tooltipText;
	}

	public show() {
		const elem = this.getElement();
		const displayElem = this.getDisplayElement();
		if (
			displayElem !== undefined
			&& !elem.classList.contains("isDisabled")
			&& !elem.hasAttribute("disabled")
		) {
			elem.ownerDocument.body.appendChild(displayElem);
		}
	}

	public hide() {
		const displayElem = this.getDisplayElement();
		if (displayElem !== undefined) {
			displayElem.remove();
			this.displayElem = undefined;
		}
	}
}
